/**
 * MarketingJourneysPage — journeys-lijst + nieuwe journey aanmaken.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMarketingStore, marketingFetch, type MarketingJourney } from '../../stores/marketing-store';
import { MarketingNav, MarketingPageHeader, StatusBadge } from './shared';

export function triggerLabel(trigger: { type: string; months?: number; days?: number; segment_key?: string }): string {
  if (trigger.type === 'months_since_last_service') return `${trigger.months} maanden na laatste service`;
  if (trigger.type === 'days_since_last_service') return `${trigger.days} dagen na laatste service`;
  if (trigger.type === 'segment_entry') return `instroom in segment '${trigger.segment_key}'`;
  return 'handmatig';
}

function NieuweJourneyModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { templates, fetchTemplates } = useMarketingStore();
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [triggerType, setTriggerType] = useState<
    'months_since_last_service' | 'days_since_last_service' | 'segment_entry' | 'manual'
  >('months_since_last_service');
  const [months, setMonths] = useState(11);
  const [days, setDays] = useState(7);
  const [segmentKey, setSegmentKey] = useState('');
  const [steps, setSteps] = useState<Array<{ name: string; wait_days: number; template_id: string }>>([
    { name: 'Stap 1', wait_days: 0, template_id: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const trigger =
        triggerType === 'months_since_last_service'
          ? { type: triggerType, months }
          : triggerType === 'days_since_last_service'
            ? { type: triggerType, days }
            : triggerType === 'segment_entry'
              ? { type: triggerType, segment_key: segmentKey }
              : { type: triggerType };
      const data = await marketingFetch<{ journey: MarketingJourney }>('/api/marketing/journeys', {
        method: 'POST',
        body: {
          key,
          name,
          trigger,
          steps: steps.map((s, i) => ({ ...s, step_order: i + 1 })),
        },
      });
      onCreated(data.journey.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aanmaken mislukt');
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm';
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Nieuwe journey</h2>
        <p className="text-xs text-gray-400 mb-4">
          Bij activering stromen alleen contacten in die het trigger-venster vanaf dán bereiken — geen
          massa-enrollment met terugwerkende kracht.
        </p>
        {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">{error}</div>}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Key (uniek, kebab-case)</label>
              <input type="text" value={key} onChange={(e) => setKey(e.target.value)} className={inputCls} placeholder="jaarlijkse-reiniging" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Naam</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Jaarlijkse reiniging" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Trigger</label>
            <select value={triggerType} onChange={(e) => setTriggerType(e.target.value as typeof triggerType)} className={`${inputCls} bg-white`}>
              <option value="months_since_last_service">X maanden na laatste service</option>
              <option value="days_since_last_service">X dagen na laatste service</option>
              <option value="segment_entry">Instroom in segment</option>
              <option value="manual">Handmatig (via API/knop)</option>
            </select>
          </div>
          {triggerType === 'months_since_last_service' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Aantal maanden</label>
              <input type="number" min={1} value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10) || 1)} className={inputCls} />
            </div>
          )}
          {triggerType === 'days_since_last_service' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Aantal dagen</label>
              <input type="number" min={1} value={days} onChange={(e) => setDays(parseInt(e.target.value, 10) || 1)} className={inputCls} />
            </div>
          )}
          {triggerType === 'segment_entry' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Segment-key</label>
              <input type="text" value={segmentKey} onChange={(e) => setSegmentKey(e.target.value)} className={inputCls} placeholder="kleda-dormant" />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Stappen</label>
            <div className="space-y-2">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={step.name}
                    onChange={(e) => setSteps(steps.map((s, j) => (j === i ? { ...s, name: e.target.value } : s)))}
                    className={`${inputCls} flex-1`}
                    placeholder={`Stap ${i + 1}`}
                  />
                  <input
                    type="number"
                    min={0}
                    value={step.wait_days}
                    onChange={(e) => setSteps(steps.map((s, j) => (j === i ? { ...s, wait_days: parseInt(e.target.value, 10) || 0 } : s)))}
                    className="w-20 border border-gray-300 rounded-lg px-2 py-2 text-sm"
                    title="Wachtdagen na vorige stap"
                  />
                  <select
                    value={step.template_id}
                    onChange={(e) => setSteps(steps.map((s, j) => (j === i ? { ...s, template_id: e.target.value } : s)))}
                    className="flex-1 border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white"
                  >
                    <option value="">— template —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {steps.length > 1 && (
                    <button onClick={() => setSteps(steps.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600 text-sm px-1">
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={() => setSteps([...steps, { name: `Stap ${steps.length + 1}`, wait_days: 7, template_id: '' }])}
              className="mt-2 text-sm text-indigo-600 hover:underline"
            >
              + Stap toevoegen
            </button>
            <p className="text-xs text-gray-400 mt-1">Wachtdagen = dagen ná de vorige stap (stap 1: ná enrollment).</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
            Annuleren
          </button>
          <button
            onClick={create}
            disabled={saving || !key || !name || steps.some((s) => !s.template_id)}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Aanmaken…' : 'Aanmaken (als concept)'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MarketingJourneysPage() {
  const navigate = useNavigate();
  const { journeys, isLoading, error, fetchJourneys } = useMarketingStore();
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    fetchJourneys();
  }, [fetchJourneys]);

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <MarketingPageHeader
          title="Journeys"
          subtitle="Automatische e-mailreeksen — enrollment, stappen en exits draaien via de engine"
          actions={
            <button onClick={() => setShowNew(true)} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">
              + Nieuwe journey
            </button>
          }
        />
        <MarketingNav />

        <div className="p-6">
          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}
          {isLoading && journeys.length === 0 ? (
            <p className="text-sm text-gray-400">Laden…</p>
          ) : journeys.length === 0 ? (
            <p className="text-sm text-gray-400">Nog geen journeys — maak bijvoorbeeld de jaarlijkse-reiniging-journey aan.</p>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {journeys.map((j) => {
                const active = j.stateCounts?.active ?? 0;
                const completed = j.stateCounts?.completed ?? 0;
                return (
                  <Link key={j.id} to={`/marketing/journeys/${j.id}`} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{j.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Trigger: {triggerLabel(j.trigger)} · {active} actief · {completed} afgerond
                      </p>
                    </div>
                    <StatusBadge status={j.status} />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showNew && (
        <NieuweJourneyModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            navigate(`/marketing/journeys/${id}`);
          }}
        />
      )}
    </>
  );
}
